import * as React from 'react'
import Card from '@mui/material/Card'
import Slide from '@mui/material/Slide'
import Box from '@mui/material/Box/Box'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useData } from './hooks/useData'
import { useHandlers } from './hooks/useHandlers'
import Alert from '@mui/material/Alert'
import { LightTooltip } from '@sales-center/components'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'

const RECORD_TYPES = [
  { key: 'draftlead', label: 'Pending Leads' },
  { key: 'lead', label: 'Leads' },
  { key: 'opportunity', label: 'Opportunities' },
]

type Props = {
  show: boolean
  onClose: () => void
  closeAllDrawers: () => void
}

function FilterDrawer(props: Props): JSX.Element {
  const { onClose, show, closeAllDrawers } = props
  const {
    selectedTypes,
    setSelectedTypes,
    myRecords,
    setMyRecords,
    showFilterChips,
    setShowFilterChips,
    showValidation,
    setShowValidation,
    initialSelectedTypes,
    initialMyRecords,
    initialSelectedKnockStatuses,
    position,
    d2dKnockEnabled,
    knockStatuses,
    selectedKnockStatuses,
    setSelectedKnockStatuses,
  } = useData(show)

  const {
    handleCheckboxChange,
    handleKnockStatusCheckboxChange,
    onChangeMyRecords,
    onChangeShowFilterChips,
    handleViewResults,
  } = useHandlers({
    selectedTypes,
    setSelectedTypes,
    myRecords,
    setMyRecords,
    showFilterChips,
    setShowFilterChips,
    setShowValidation,
    initialSelectedTypes,
    initialMyRecords,
    initialSelectedKnockStatuses,
    selectedKnockStatuses,
    setSelectedKnockStatuses,
    closeAllDrawers,
    onClose,
    d2dKnockEnabled,
    knockStatuses,
  })

  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const isMobile = useMediaQuery('(max-width: 600px)')

  const { left, top } = position

  return (
    <Slide direction="right" in={show} mountOnEnter unmountOnExit>
      <Card
        sx={{
          width: isMobile ? '100vw' : 400,
          position: 'fixed',
          top,
          left,
          bottom: 0,
          zIndex: 1300,
          boxShadow: 4,
          borderRadius: undefined,
          overflowY: 'auto',
          backgroundColor: 'background.default',
        }}
      >
        <Box
          sx={{
            paddingLeft: 3,
            paddingRight: 2,
            pb: 1.5,
            paddingY: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.default',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="subtitle3">Filter</Typography>

            <Button
              variant="text"
              aria-label="Cancel"
              onClick={onClose}
              sx={{ color: '#1565C0', minWidth: 0, width: 'fit-content' }}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        </Box>

        <Stack sx={{ paddingX: 3, width: '100%' }}>
          <Typography variant="subtitle3" sx={{ mt: 1.5 }}>
            Filter visibility
          </Typography>

          <FormControlLabel
            sx={{
              ml: 0,
              mb: 1,
              mt: 1,
            }}
            labelPlacement="end"
            label={
              <Typography variant="body1">
                Display filter chips on map
              </Typography>
            }
            control={
              <Switch
                size="medium"
                checked={showFilterChips}
                onChange={onChangeShowFilterChips}
              />
            }
          />

          <Divider sx={{ mb: 1.5, mt: 0 }} />

          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
            >
              <Typography variant="subtitle3">Records</Typography>
              <LightTooltip
                title={
                  <span>
                    Selecting <strong>Records</strong> only will show pins
                    without a knock status. Selecting{' '}
                    <strong>Knock Statuses</strong> only will show addresses not
                    associated with a record. Selecting <strong>both</strong>{' '}
                    will combine these views to show all pins and knock statuses
                    based on your selections.
                  </span>
                }
                placement="left"
              >
                <IconButton
                  size="small"
                  sx={{
                    padding: 0,
                    minWidth: 24,
                    minHeight: 24,
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 18 }}
                    data-test-id="mapFilterDrawerInfoIcon"
                  />
                </IconButton>
              </LightTooltip>
            </Box>
            <FormGroup>
              {!salesPersonFilterEnabled && (
                <FormControlLabel
                  sx={{ ml: 0, mb: 1, mt: 1, root: { mb: 1 } }}
                  labelPlacement="end"
                  label={
                    <Typography variant="body1">
                      View my records only
                    </Typography>
                  }
                  control={
                    <Switch
                      size="medium"
                      checked={myRecords}
                      onChange={onChangeMyRecords}
                    />
                  }
                />
              )}
              {RECORD_TYPES.map((rt) => (
                <FormControlLabel
                  key={rt.key}
                  sx={{ ml: 0, mb: 0 }}
                  control={
                    <Checkbox
                      checked={selectedTypes.includes(rt.key)}
                      onChange={() => handleCheckboxChange(rt.key)}
                    />
                  }
                  label={<Typography variant="body1">{rt.label}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>

          {d2dKnockEnabled && knockStatuses.length > 0 && (
            <>
              <Divider sx={{ mb: 1.5, mt: 1 }} />
              <Box>
                <Typography variant="subtitle3" sx={{ mb: 1 }}>
                  Knock Statuses
                </Typography>
                <FormGroup>
                  {knockStatuses.map((status) => (
                    <FormControlLabel
                      key={status.id}
                      sx={{ ml: 0, mb: 0 }}
                      control={
                        <Checkbox
                          checked={selectedKnockStatuses.includes(
                            String(status.id),
                          )}
                          onChange={() =>
                            handleKnockStatusCheckboxChange(String(status.id))
                          }
                        />
                      }
                      label={
                        <Typography variant="body1">{status.name}</Typography>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
            </>
          )}

          {showValidation && (
            <>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Stack mb={2}>
                <Alert severity={'warning'} sx={{ alignItems: 'flex-start' }}>
                  You must select at least one record type or knock status to
                  view results.
                </Alert>
              </Stack>
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.5,
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.default',
              borderTop: 1,
              borderColor: 'divider',
              mx: -3,
              px: 3,
              py: 1.5,
              zIndex: 1,
              mt: showValidation ? 0 : 1,
            }}
          >
            <Button
              variant="contained"
              aria-label="view-results"
              onClick={handleViewResults}
              size="large"
            >
              View results
            </Button>
          </Box>
        </Stack>
      </Card>
    </Slide>
  )
}

export default FilterDrawer
